import { Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { SubHeader } from "src/apps/commons/dataDisplays";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import { listAllPartners } from "src/apps/commons/dropdowns/api";
import { FileUpload } from "src/apps/commons/fileUploads";
import { IPartnerBrief } from "src/apps/modules/partner/types";
import { ISSWUploadPreviewReq } from "../types";

const useStyles = makeStyles(theme => ({
  alert: {
    marginTop: theme.spacing(1)
  },
  ul: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
    "&> ul": {
      marginTop: 4,
      marginBottom: 4
    }
  }
}));

type Props = {
  submitting: boolean;
  onChanged: (formState: Partial<ISSWUploadPreviewReq>) => void;
};

const ColumnRemark = (props: { col: string; desc: string; code?: string }) => (
  <li>
    <strong>{props.col}</strong> : {props.desc}
    {props.code && " "}
    {props.code && (
      <Typography variant="caption" color="primary">
        {props.code}
      </Typography>
    )}
  </li>
);

const UploadSSWForm: React.FC<Props> = props => {
  const classes = useStyles();
  const { submitting, onChanged } = props;

  const [partner, setPartner] = useState<IDropdown<IPartnerBrief> | null>(null);
  const [basicViewFileId, setBasicViewFileId] =
    useState<FileUploadId | undefined>();
  const [salesViewFileId, setSalesViewFileId] =
    useState<FileUploadId | undefined>();

  useEffect(() => {
    onChanged({
      partnerId: partner?.value,
      basicViewFileId: basicViewFileId,
      salesViewFileId: salesViewFileId
    });
  }, [partner, basicViewFileId, salesViewFileId, onChanged]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SubHeader title="Upload" marginTop />
      </Grid>
      <Grid item xs={12}>
        <AsyncDropdown
          label={"Partner *"}
          fetchDropdown={listAllPartners}
          paginate={{
            page: 1,
            pageSize: 500,
            order: {
              optionKey: "NAME",
              desc: false
            }
          }}
          value={partner}
          onChange={setPartner}
          disabled={submitting}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <FileUpload
          label={"Basic View Excel *"}
          fileId={basicViewFileId}
          onChange={setBasicViewFileId}
          isShow={submitting}
        />
        <Alert severity="info" className={classes.alert}>
          ข้อมูล Basic View ประกอบไปด้วย:
          <ul className={classes.ul}>
            <ColumnRemark
              col="A"
              desc="Z Code (ต้อง Unique)"
              code="→ partner_product.code & .ref_product_code"
            />
            <ColumnRemark
              col="B"
              desc="ชื่อสินค้า (EN)"
              code="→ partner_product.name_en"
            />
            <ColumnRemark
              col="C"
              desc="ชื่อสินค้า (TH)"
              code="→ partner_product.name_th"
            />
            <ColumnRemark
              col="D"
              desc="EAN (ถ้าไม่ระบุ จะไม่เอาเข้า)"
              code="→ partner_product.barcode"
            />
          </ul>
        </Alert>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FileUpload
          label={"Sales View Excel *"}
          fileId={salesViewFileId}
          onChange={setSalesViewFileId}
          isShow={submitting}
        />
        <Alert severity="info" className={classes.alert}>
          ข้อมูล Sales View ประกอบไปด้วย:
          <ul className={classes.ul}>
            <ColumnRemark
              col="A"
              desc="Z Code (ต้อง Unique)"
              code="→ partner_product.code & .ref_product_code"
            />
            <ColumnRemark col="B" desc="ชื่อสินค้า (ไม่สนใจ)" />
            <ColumnRemark col="C-E" desc="Mat Group 1-3 (ไม่สนใจ)" />
            <ColumnRemark
              col="F"
              desc="Mat Group 4 (หมวดสินค้า Lv3)"
              code="→ partner_product.category_lv3_id"
            />
          </ul>
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Alert severity="info">
          หลักการนำเข้าประกอบไปด้วย:
          <ul className={classes.ul}>
            <li>{`สินค้า จะไม่นำเข้า ถ้า`}</li>
            <ul>
              <li>{`ไม่มี EAN`}</li>
              <li>{`หรือ ไม่มีหมวดหมู่ (ไม่พบใน Sales View หรือพบแต่ไม่ระบุ MatGroup4)`}</li>
            </ul>
            <li>
              {`สินค้า ต่อ 1 ZCode ถ้ามี MatGroup4 มากกว่า 1 รายการ จะนำตัวที่น้อยที่สุดเข้า (Sort A-Z)`}
            </li>
            <li>{`สินค้า จะ Cancel เช็คจาก ชื่อไทยหรืออังกฤษแบบ Uppercase โดย`}</li>
            <ul>
              <li>{`มีคำว่า CANCEL อยู่ในชื่อ`}</li>
              <li>{`หรือ ขึ้นต้นด้วย CANC`}</li>
            </ul>

            <li>{`สินค้า จะ Inactive ถ้าพบ * ในชื่อไทยหรืออังกฤษ`}</li>
            <li>{`หมวดหมู่`}</li>
            <ul>
              <li>
                {`Lookup เจอใน Sales View แต่ไม่เจอใน Master PartnerCategory 
                จะสร้างหมวดหมู่ COTTO > COTTO > [MatGroup4] ให้`}
              </li>
              <li>
                {`Lookup เจอใน Sales View และเจอใน Master PartnerCategory 
                แต่ยังไม่ได้ทำ Remynd Mapping จะได้สินค้าฝั่ง Partner มีหมวดหมู่
                แต่ Sync มาฝั่ง Remynd ไม่มีหมวดหมู่`}
              </li>
            </ul>
          </ul>
        </Alert>
      </Grid>
    </Grid>
  );
};

export default UploadSSWForm;
